import React, { useRef, useState } from "react";
import { graphql, Link, useStaticQuery } from "gatsby";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";

import Layout from "../../components/Layout";
import SearchEngineOptimization from "../../components/SEO";
import CallToAction from "../../components/Repeating/CTA";

import ButtonSolid from "../../components/Button/ButtonSolid";
import Attorneys from "../../components/Repeating/Attorneys";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slick from "react-slick";
import { AnchorLink } from "gatsby-plugin-anchor-links";

const glassdoorReviews = [
  {
    author: "Associate Attorney",
    text: (
      <>
        <p>
          The best thing about this company is the people. Everyone from the receptionists to the
          partners was friendly and helpful. It is truly an open-door policy.
        </p>
        <p>
          They also have a great remote work policy. Most people in the office have children, and
          the workplace is understanding of family emergencies. They also have great benefits,
          especially their PTO policy. The office culture is positive, and the firm's policies
          encourage team building.
        </p>
      </>
    ),
  },
  {
    author: "Associate Attorney",
    text: (
      <>
        <p>
          Great work-life balance with a direct path to becoming a shareholder and controlling your
          own case load.
        </p>
      </>
    ),
  },
  {
    author: "Paralegal",
    text: (
      <>
        <p>
          The staff is wonderful to work with! Your questions are always answered, and just about
          everyone is willing to jump in and help out if needed. Expectations are high, but you are
          given all the tools needed to succeed. This is an amazing firm to work for with lots of
          opportunities for growth.
        </p>
      </>
    ),
  },
  {
    author: "Anonymous Employee",
    text: (
      <>
        <ul className="list-disc ml-4">
          <li>Collaboration</li>
          <li>Communication</li>
          <li>Team environment</li>
          <li>Career development</li>
          <li>Firm-wide contributions to local communities through volunteer opportunities</li>
          <li>Supportive shareholders & managers</li>
          <li>Benefits package</li>
          <li>Advancement opportunities from within</li>
        </ul>
      </>
    ),
  },
];

const Page = ({ data }) => {
  const sliderRef = useRef();
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    fade: true,
    speed: 750,
    autoplay: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    afterChange: (current) => setCurrentSlide(current),
    // prevArrow: <PrevArrow />,
    // nextArrow: <NextArrow />,
  };

  // Previous Arrow
  function PrevArrow(props) {
    const { sliderRef, currentSlide } = props;
    return (
      <button
        onClick={() => sliderRef.current.slickPrev()}
        className="group grid grid-cols-1 grid-rows-1"
        aria-label="Previous Slide"
      >
        <StaticImage
          src="../../images/3.0 Careers/left-arrow.svg"
          width={64}
          className="transition-all duration-300 col-start-1 col-span-1 row-span-1 row-start-1 opacity-100 group-hover:opacity-0"
        />
        <StaticImage
          src="../../images/3.0 Careers/left-arrow-hover.svg"
          width={64}
          className="opacity-0 col-start-1 col-span-1 row-span-1 row-start-1 group-hover:opacity-100"
        />
      </button>
    );
  }

  // Next Arrow
  function NextArrow(props) {
    const { sliderRef, currentSlide } = props;
    return (
      <button
        onClick={() => sliderRef.current.slickNext()}
        className="group grid grid-cols-1 grid-rows-1"
        aria-label="Next Slide"
      >
        <StaticImage
          src="../../images/3.0 Careers/left-arrow.svg"
          width={64}
          className="rotate-180 transition-all duration-300 col-start-1 col-span-1 row-span-1 row-start-1 opacity-100 group-hover:opacity-0"
        />
        <StaticImage
          src="../../images/3.0 Careers/left-arrow-hover.svg"
          width={64}
          className="rotate-180 opacity-0 col-start-1 col-span-1 row-span-1 row-start-1 group-hover:opacity-100"
        />
      </button>
    );
  }

  return (
    <Layout>
      <SearchEngineOptimization
        title="Legal Careers at JBP Legal | Join Our Team of Professionals"
        description="Explore career opportunities at JBP Legal. We're always looking for talented attorneys, paralegals, and legal staff to join our growing team. Start your legal career with us today."
        openGraphImage={data.openGraphImage.publicURL}
        twitterOpenGraphImage={data.twitterOpenGraphImage.publicURL}
      />

      <section className="relative pb-20 md:pb-28">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-y-8">
            <div className="md:pt-32 md:pb-14 md:pr-18 order-2 md:order-1">
              <div className="bg-gray-900 text-gray-300 uppercase px-2.5 py-1.5 inline-flex items-center justify-center mb-5 md:mb-6">
                Since 1989
              </div>
              <h1>Join the JBP Legal Team</h1>
              <div className="md:pt-3.5">
                <p className="mb-8">
                  Help shape Colorado’s legal landscape with a firm that values integrity, teamwork,
                  and professional growth.
                </p>
                <AnchorLink to={"#open-positions"}>
                  <ButtonSolid
                    // modal="modal-contact"
                    // href="#open-positions"
                    text="View Open Positions"
                    className="w-full md:w-auto"
                  />
                </AnchorLink>
              </div>
            </div>
            <div className="order-1 md:order-2 relative -mx-4 md:mx-0">
              <div className="md:left-0 md:w-full h-full">
                <GatsbyImage
                  image={data.hero.childImageSharp.gatsbyImageData}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative pb-20 md:pb-28">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="md:pt-32 md:pb-14 md:pr-18 order-2 md:order-2">
              <h2>Work With Purpose, Legal Expertise Without Compromise</h2>
              <div className="md:pt-3.5">
                <p className="mb-8">
                  At Jorgensen, Brownell & Pepin, P.C., we’ve spent over 30 years building a legal
                  firm grounded in trust, strategic thinking, and collaborative problem-solving.
                </p>{" "}
                <p className="mb-8">
                  Our team is made up of smart and passionate professionals who care deeply about
                  their work—and each other. If you’re ready to grow your legal career in a
                  supportive environment that values balance, integrity, and expertise, we’d love to
                  hear from you.
                </p>
                <AnchorLink to={"#open-positions"}>
                  <ButtonSolid
                    // modal="modal-contact"
                    // href="#open-positions"
                    text="View Open Positions"
                    className="w-full md:w-auto"
                  />
                </AnchorLink>
              </div>
            </div>
            <div className="order-1 md:order-1 relative md:h-full -mx-4 md:mx-0 w-full ">
              <div className="w-full md:left-0 md:w-full h-full">
                <GatsbyImage
                  image={data.intro.childImageSharp.gatsbyImageData}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <video width="100%" className="max-w-[1200px] mx-auto my-20" controls>
        <source
          src="https://player.vimeo.com/external/659082300.hd.mp4?s=6dd5f938c63496ec4517f4331af55180ed4024e7&profile_id=175"
          type="video/mp4"
        />
      </video>

      <section className="max-w-[1200px] mx-auto w-full flex flex-col gap-y-10 px-4 my-20 lg:my-[120px]">
        <h2>What Sets Us Apart</h2>
        <div className="flex flex-col lg:grid lg:grid-cols-4 lg:gap-x-10 gap-y-6">
          <div className="">
            <StaticImage src="../../images/3.0 Careers/001.svg" width={60} />
            <h3 className="font-heading font-bold leading-[27px] text-[20px] mt-6">
              30+ Years of Experience
            </h3>
            <p>
              Over the last three decades, we’ve built a reputation not just for legal excellence,
              but for the way we treat people. Our track record speaks for itself. We encourage you
              to see what our <a href="https://www.jbplegal.com/testimonials/">satisfied clients</a>{" "}
              and{" "}
              <a
                href="https://www.glassdoor.com/Reviews/Jorgensen-Brownell-and-Pepin-Reviews-E1104301.htm"
                target="_blank"
              >
                employees
              </a>{" "}
              say about us.{" "}
            </p>
          </div>
          <div>
            <StaticImage src="../../images/3.0 Careers/001.svg" width={60} />
            <h3 className="font-heading font-bold leading-[27px] text-[20px] mt-6">
              Custom Legal Solutions
            </h3>
            <div>
              <p>
                While we believe in upholding Colorado law, we don’t believe in one-size-fits-all
                solutions. We value diverse perspectives and empower our team members to bring
                creative solutions to every case. Here, your voice and ideas matter.
              </p>
            </div>
          </div>
          <div>
            <StaticImage src="../../images/3.0 Careers/001.svg" width={60} />
            <h3 className="font-heading font-bold leading-[27px] text-[20px] mt-6">
              Deep Experience Across Disciplines
            </h3>
            <p>
              We don’t just do one thing well. Our strength lies in the range and depth of our legal
              knowledge. At JBP, you’ll be surrounded by experts in a range of disciplines,
              including family law, estate planning, personal injury, real estate, and more.
            </p>
          </div>
          <div>
            <StaticImage src="../../images/3.0 Careers/001.svg" width={60} />
            <h3 className="font-heading font-bold leading-[27px] text-[20px] mt-6">
              Compassionate Guidance
            </h3>
            <p>
              We believe in efficiency, but not at the expense of kindness and compassion. Our team
              is dedicated to helping clients—and each other—overcome common legal obstacles with
              both intellect and heart.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto w-full flex flex-col gap-y-10 px-4 my-20 lg:my-[120px]">
        <div className="max-w-[800px]">
          <h2>Local Roots, Regional Reach</h2>
          <p>
            We are proud to provide high-quality legal services to individuals in the Denver Metro
            Area and throughout Northern Colorado.
          </p>
          <p>
            Our four strategic locations in Broomfield, Longmont, Loveland, and Denver don’t just
            keep us connected to our local community—they give our team greater flexibility in where
            they work, who they serve, and how they grow within the firm.
          </p>
        </div>
        <div className="lg:grid grid-cols-2 flex flex-col gap-8">
          <div className="flex w-full gap-x-4">
            <StaticImage src="../../images/3.0 Careers/denver.png" width={176} />
            <div>
              <h3 className="font-heading font-bold leading-[27px] text-[20px] mt-6">
                Denver Office
              </h3>
              <p>
                3461 Ringsby Ct., Suite 350
                <br />
                Denver, CO 80216
                <br />
                (303) 678-0560
              </p>
            </div>
          </div>{" "}
          <div className="flex w-full gap-x-4">
            <StaticImage src="../../images/3.0 Careers/broomfield.png" width={176} />
            <div>
              <h3 className="font-heading font-bold leading-[27px] text-[20px] mt-6">
                Broomfield Office
              </h3>
              <p>
                8001 Arista Pl., Suite 415
                <br />
                Broomfield, CO 80021
                <br />
                (970) 304-0075
                <br />
              </p>
            </div>
          </div>{" "}
          <div className="flex w-full gap-x-4">
            <StaticImage src="../../images/3.0 Careers/longmont.png" width={176} />
            <div>
              <h3 className="font-heading font-bold leading-[27px] text-[20px] mt-6">
                Longmont Office
              </h3>
              <p>
                900 S Main St. Suite 100
                <br />
                Longmont, CO 80501
                <br />
                (720) 603-1773
                <br />
              </p>
            </div>
          </div>{" "}
          <div className="flex w-full gap-x-4">
            <StaticImage src="../../images/3.0 Careers/loveland.png" width={176} />
            <div>
              <h3 className="font-heading font-bold leading-[27px] text-[20px] mt-6">
                Loveland Office
              </h3>
              <p>
                5285 McWhinney Blvd. Suite 100
                <br />
                Loveland, CO 80538
                <br />
                (970) 644-6275
                <br />
              </p>
            </div>
          </div>
        </div>
      </section>
      <Attorneys />

      <section className="lg:mb-[120px] mb-20 px-4">
        <div className="mx-auto max-w-[1200px] w-full flex flex-col-reverse gap-10 lg:grid lg:grid-cols-2">
          <div className="w-full">
            <StaticImage src="../../images/3.0 Careers/glassdoor-logo.png" width={117} />
            <h2 className="mt-4">
              What Our Employees Say <br />
              About Us
            </h2>
            <Slick {...sliderSettings} ref={sliderRef} className="h-fit mt-10">
              {glassdoorReviews.map((review, index) => (
                <div key={index}>
                  <div>{review.text}</div>
                  <div className="text-white font-body font-semibold leading-[24px] mt-10">
                    {review.author}
                  </div>
                </div>
              ))}
            </Slick>
            <div className="w-full flex items-end mt-16 justify-between">
              <div className="font-display text-sm font-bold uppercase flex items-center">
                <div className="text-white">0{currentSlide + 1}</div>
                <div className="text-white/20">/0{glassdoorReviews.length}</div>
              </div>
              <div className="w-fit flex items-center gap-x-3">
                <PrevArrow sliderRef={sliderRef} currentSlide={currentSlide} />
                <NextArrow sliderRef={sliderRef} currentSlide={currentSlide} />
              </div>
            </div>
          </div>
          <div className="w-full">
            <GatsbyImage
              image={data.glassdoor.childImageSharp.gatsbyImageData}
              className="w-full h-full"
            />
          </div>
        </div>
      </section>

      <section className="px-4 my-20 lg:my-[120px]" id="open-positions">
        <div className="w-full max-w-[880px] mx-auto">
          <h2 className="mt-4">Open Positions</h2>
          <div className="flex flex-col border-t-[1px] border-primary-600">
            {data.positions?.edges && data.positions.edges.length > 0 ? (
              data.positions.edges.map((position, index) => (
                <Link
                  to={position.node.slug.current}
                  key={index}
                  className="group flex items-center w-full justify-between py-7 border-b-[1px] border-primary-600 font-heading text-[24px] font-bold leading-[34px] hover:text-primary-600"
                >
                  {position.node.title}
                  <div
                    className="grid grid-cols-1 grid-rows-1 w-16 h-16 shrink-0"
                    aria-label="Next Slide"
                  >
                    <StaticImage
                      src="../../images/3.0 Careers/left-arrow.svg"
                      width={64}
                      className="rotate-180 transition-all duration-300 col-start-1 col-span-1 row-span-1 row-start-1 opacity-100 group-hover:opacity-0"
                    />
                    <StaticImage
                      src="../../images/3.0 Careers/left-arrow-hover.svg"
                      width={64}
                      className="rotate-180 opacity-0 col-start-1 col-span-1 row-span-1 row-start-1 group-hover:opacity-100"
                    />
                  </div>
                </Link>
              ))
            ) : (
              <h4 className="py-7">There are no open positions at this time.</h4>
            )}
          </div>
        </div>
      </section>

      <CallToAction />
    </Layout>
  );
};

export const data = graphql`
  {
    positions: allSanityPosition(
      sort: { fields: [publishedAt], order: DESC }
      filter: { slug: { current: { ne: null } }, publishedAt: { ne: null } }
    ) {
      edges {
        node {
          id
          title
          slug {
            current
          }
        }
      }
    }
    openGraphImage: file(relativePath: { eq: "open-graph/facebook/Practice Hub.jpg" }) {
      publicURL
    }
    twitterOpenGraphImage: file(relativePath: { eq: "open-graph/twitter/Practice Hub.jpg" }) {
      publicURL
    }
    hero: file(relativePath: { eq: "3.0 Careers/hero.jpg" }) {
      childImageSharp {
        gatsbyImageData(layout: CONSTRAINED, width: 685)
      }
    }
    intro: file(relativePath: { eq: "3.0 Careers/intro.png" }) {
      childImageSharp {
        gatsbyImageData(layout: CONSTRAINED, width: 560)
      }
    }
    glassdoor: file(relativePath: { eq: "3.0 Careers/glasdoor.png" }) {
      childImageSharp {
        gatsbyImageData(layout: CONSTRAINED, width: 560)
      }
    }
  }
`;

export default Page;
